import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import ConsoleLayout from "@/components/ConsoleLayout";
import BillSummary from "@/components/billing/BillSummary";
import BillItems from "@/components/billing/BillItems";
import { Button } from "@/components/ui/button";
import PreviewBillModal from "./components/PreviewBillModal";
import { adminApi } from "@/lib/api";
import type { AdminTeamSummary, TeamBill, TeamBillItem } from "@/lib/types";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { ChevronDown, ChevronLeft, ChevronRight, Download, X, CheckCircle2, Ban, Eye } from "lucide-react";
import { cn, formatUSD } from "@/lib/utils";

type StatusFilter = "" | "unpaid" | "paid" | "void";

const PAGE_SIZE = 20;

export default function AdminBills() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const search = useSearch();
  const [, navigate] = useLocation();

  // ?team=<id> seeds the filter (AdminTeams links here via the row's Bills
  // icon). Read once on mount — the user can clear or change it afterwards
  // without the URL fighting back.
  const initialTeam = useMemo(
    () => new URLSearchParams(search).get("team") || "",
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [teamFilter, setTeamFilter] = useState(initialTeam);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [teams, setTeams] = useState<AdminTeamSummary[]>([]);
  const [bills, setBills] = useState<TeamBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailItems, setDetailItems] = useState<Record<string, TeamBillItem[]>>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const teamMap = useMemo(() => {
    const m: Record<string, string> = {};
    teams.forEach((ts) => {
      m[ts.team.id] = ts.team.name;
    });
    return m;
  }, [teams]);

  // Full team listing backs the filter dropdown + id -> name lookup for the
  // table (the bills API only returns team_id).
  useEffect(() => {
    if (!user?.is_system_admin) return;
    adminApi
      .listAllTeams({ page: 1, page_size: 200 })
      .then((resp) => setTeams(resp.teams))
      .catch(() => {
        /* ignore — dropdown just stays empty, table still shows raw ids */
      });
  }, [user?.is_system_admin]);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await adminApi.listBills({
        team_id: teamFilter || undefined,
        status: statusFilter || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setBills(res.bills);
      setTotal(res.total);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load bills");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamFilter, statusFilter, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const clearTeamFilter = () => {
    setTeamFilter("");
    setPage(1);
    navigate("/console/admin/bills");
  };

  const toggleExpand = async (bill: TeamBill) => {
    if (expandedId === bill.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(bill.id);
    if (!detailItems[bill.id]) {
      setDetailLoading(bill.id);
      try {
        const full = await adminApi.getBill(bill.id);
        setDetailItems((prev) => ({ ...prev, [bill.id]: full.items || [] }));
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Failed to load bill detail");
      } finally {
        setDetailLoading(null);
      }
    }
  };

  const handlePay = async (bill: TeamBill) => {
    const ok = window.confirm(
      `确定将该账单标记为已结清吗？\n团队: ${teamMap[bill.team_id] || bill.team_id}\n应付额: ${formatUSD(bill.amount_due)}\n\n` +
        `Mark this bill as paid? This cannot be undone.`,
    );
    if (!ok) return;
    setBusyId(bill.id);
    try {
      await adminApi.payBill(bill.id);
      toast.success(t("admin.bills.paidToast"));
      await refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Operation failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleVoid = async (bill: TeamBill) => {
    const ok = window.confirm(
      `确定要作废该账单吗？此操作不可撤销。\n团队: ${teamMap[bill.team_id] || bill.team_id}\n\n` +
        `Void this bill? This cannot be undone.`,
    );
    if (!ok) return;
    setBusyId(bill.id);
    try {
      await adminApi.voidBill(bill.id);
      toast.success(t("admin.bills.voidedToast"));
      await refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Operation failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleExport = async (bill: TeamBill) => {
    try {
      await adminApi.downloadBillCsv(bill.id);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Export failed");
    }
  };

  const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString() : "—");
  const fmtDateTime = (s?: string) => (s ? new Date(s).toLocaleString() : "—");

  const statusLabel = (status: TeamBill["status"]) =>
    status === "unpaid"
      ? t("admin.bills.statusUnpaid")
      : status === "paid"
        ? t("admin.bills.statusPaid")
        : t("admin.bills.statusVoid");

  const statusBadge = (status: TeamBill["status"]) => (
    <span
      className={cn(
        "px-1.5 py-0.5 rounded text-[10px] font-medium",
        status === "unpaid" && "bg-amber-500/20 text-amber-400",
        status === "paid" && "bg-emerald-500/20 text-emerald-400",
        status === "void" && "bg-slate-500/20 text-slate-400",
      )}
    >
      {statusLabel(status)}
    </span>
  );

  const gridCols = "grid-cols-[1.6fr_1.6fr_1fr_1fr_90px_130px_230px]";

  return (
    <ConsoleLayout title={t("admin.bills.title")} subtitle={t("admin.bills.subtitle")}>
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={teamFilter}
            onChange={(e) => {
              setTeamFilter(e.target.value);
              setPage(1);
            }}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50 max-w-[220px]"
          >
            <option value="">{t("admin.bills.allTeams")}</option>
            {teams.map((ts) => (
              <option key={ts.team.id} value={ts.team.id}>
                {ts.team.name}
              </option>
            ))}
          </select>
          {teamFilter && (
            <button
              onClick={clearTeamFilter}
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-white px-2 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <X size={11} />
              {t("admin.bills.clearTeamFilter")}
            </button>
          )}
          <div className="flex items-center gap-1.5">
            {(["", "unpaid", "paid", "void"] as StatusFilter[]).map((s) => (
              <button
                key={s || "all"}
                onClick={() => {
                  setStatusFilter(s);
                  setPage(1);
                }}
                className={cn(
                  "text-xs px-2.5 py-1 rounded-lg transition-all",
                  statusFilter === s ? "bg-white/10 text-white" : "text-slate-500 hover:text-slate-300",
                )}
              >
                {s === "" ? t("admin.bills.allStatus") : statusLabel(s)}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPreviewOpen(true)}
            className="h-9 px-3 text-xs text-slate-300 hover:text-white border border-white/10 gap-1.5"
          >
            <Eye size={12} />
            {t("admin.bills.preview")}
          </Button>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-500">
              {total} {t("admin.bills.records")}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-slate-400">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="sci-card overflow-hidden">
          <div
            className={cn(
              "px-5 py-3 border-b border-white/8 grid gap-3 text-[10px] text-slate-600 uppercase tracking-wider items-center",
              gridCols,
            )}
          >
            <div>{t("admin.bills.colTeam")}</div>
            <div>{t("admin.bills.colPeriod")}</div>
            <div className="text-right">{t("admin.bills.colTotal")}</div>
            <div className="text-right">{t("admin.bills.colAmountDue")}</div>
            <div>{t("admin.bills.colStatus")}</div>
            <div>{t("admin.bills.colIssuedAt")}</div>
            <div className="text-right">{t("admin.bills.colActions")}</div>
          </div>
          {loading ? (
            <div className="px-5 py-10 text-center text-xs text-slate-500">Loading…</div>
          ) : bills.length === 0 ? (
            <div className="px-5 py-10 text-center text-xs text-slate-500">{t("admin.bills.empty")}</div>
          ) : (
            bills.map((bill) => (
              <div key={bill.id}>
                <div
                  onClick={() => toggleExpand(bill)}
                  className={cn(
                    "px-5 py-4 border-b border-white/5 last:border-0 grid gap-3 items-center hover:bg-white/3 transition-colors cursor-pointer",
                    gridCols,
                  )}
                >
                  <div className="min-w-0 flex items-center gap-1.5">
                    {expandedId === bill.id ? (
                      <ChevronDown size={12} className="text-slate-500 flex-shrink-0" />
                    ) : (
                      <ChevronRight size={12} className="text-slate-500 flex-shrink-0" />
                    )}
                    <span className="text-sm text-white truncate">{teamMap[bill.team_id] || bill.team_id}</span>
                  </div>
                  <div className="text-xs text-slate-300 font-mono">
                    {fmtDate(bill.period_start)} ~ {fmtDate(bill.period_end)}
                  </div>
                  <div className="text-xs text-slate-300 text-right font-mono">
                    {formatUSD(bill.total_consumption)}
                  </div>
                  <div className="text-xs text-right font-mono text-white">
                    {formatUSD(bill.amount_due)}
                  </div>
                  <div>{statusBadge(bill.status)}</div>
                  <div className="text-[10px] text-slate-500">{fmtDateTime(bill.issued_at)}</div>
                  <div className="flex items-center gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
                    {bill.status === "unpaid" && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busyId === bill.id}
                          onClick={() => handlePay(bill)}
                          className="h-7 px-2 text-[10px] text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 border border-white/10 gap-1"
                        >
                          <CheckCircle2 size={11} />
                          {t("admin.bills.markPaid")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busyId === bill.id}
                          onClick={() => handleVoid(bill)}
                          className="h-7 px-2 text-[10px] text-red-300 hover:text-red-200 hover:bg-red-500/10 border border-white/10 gap-1"
                        >
                          <Ban size={11} />
                          {t("admin.bills.void")}
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleExport(bill)}
                      className="h-7 px-2 text-[10px] text-slate-300 hover:text-white border border-white/10 gap-1"
                    >
                      <Download size={11} />
                      {t("admin.bills.export")}
                    </Button>
                  </div>
                </div>
                {expandedId === bill.id && (
                  <div className="px-5 py-4 bg-black/20 border-b border-white/5 space-y-4">
                    <BillSummary bill={bill} />
                    <div>
                      <div className="text-[10px] text-slate-600 uppercase tracking-wider px-2 pb-1">
                        {t("admin.bills.lineItems")}
                      </div>
                      {detailLoading === bill.id ? (
                        <div className="text-xs text-slate-500 py-3 text-center">Loading…</div>
                      ) : (
                        <BillItems items={detailItems[bill.id] || []} />
                      )}
                    </div>
                    {bill.note && (
                      <div className="text-[10px] text-slate-500 px-2">
                        {t("admin.bills.note")}: {bill.note}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      {previewOpen && (
        <PreviewBillModal
          teams={teams}
          initialTeamId={teamFilter || undefined}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </ConsoleLayout>
  );
}
